import { useMemo } from 'react';
import { Catalogue_FeatureFlag } from '../gql/graphql';
import {
  type PermissionUtils,
  getPermissionUtils,
  usePermissions,
} from './use-permissions';
import {
  getPersonalSettings,
  usePersonalSettings,
} from './use-personal-settings';

type FeatureFlagKeys<T> = {
  [K in keyof T]: K extends `featureFlag${string}` ? K : never;
}[keyof T];

type SettingsFeatureFlagKeys = NonNullable<
  FeatureFlagKeys<Awaited<ReturnType<typeof getPersonalSettings>>>
>;
export type FeatureFlags = Record<SettingsFeatureFlagKeys, boolean>;

function mapFeatureFlags(
  permissionUtils: PermissionUtils,
  settings: Partial<Awaited<ReturnType<typeof getPersonalSettings>>>,
) {
  return Object.entries(settings).reduce((acc, [key, value]) => {
    if (key.startsWith('featureFlag')) {
      const booleanValue =
        value === Catalogue_FeatureFlag.On ||
        (value === Catalogue_FeatureFlag.TyroOnly &&
          permissionUtils.isTyroUser);
      acc[key as SettingsFeatureFlagKeys] = booleanValue;
    }
    return acc;
  }, {} as FeatureFlags);
}

export async function getFeatureFlags() {
  const [permissionUtils, settings] = await Promise.all([
    getPermissionUtils(),
    getPersonalSettings(),
  ]);

  return mapFeatureFlags(permissionUtils, settings);
}

export function useFeatureFlags() {
  const permissions = usePermissions();
  const settings = usePersonalSettings();

  return useMemo(
    () => mapFeatureFlags(permissions, settings),
    [permissions, settings],
  );
}
